class HeroEditorialCarousel extends HTMLElement {
  connectedCallback() {
    this.heroRoot = this.closest('.hero-editorial');
    this.track = this.querySelector('.hero-editorial__track');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.captionEl = this.querySelector('[data-caption]');

    if (!this.track || this.slides.length === 0) return;

    const activeIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.index = activeIndex === -1 ? 0 : activeIndex;
    this.paused = false;
    this.timer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayMs = this.getAutoplayMs();
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion;

    this.captureDefaults();
    this.applyOverrides(this.slides[this.index]);
    this.bindEvents();
    this.startAutoplay();
    this.setupParallax();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.teardownParallax();
    if (this.handleVisibility) document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  getAutoplayMs() {
    if (!this.heroRoot) return 6000;
    const value = parseInt(getComputedStyle(this.heroRoot).getPropertyValue('--hero-autoplay-ms'), 10);
    return Number.isFinite(value) && value > 0 ? value : 6000;
  }

  captureDefaults() {
    this.headingEl = this.heroRoot ? this.heroRoot.querySelector('[data-hero-heading]') : null;
    this.descriptionEl = this.heroRoot ? this.heroRoot.querySelector('[data-hero-description]') : null;
    this.primaryBtn = this.heroRoot ? this.heroRoot.querySelector('[data-hero-primary-btn]') : null;
    this.primaryLabelEl = this.heroRoot ? this.heroRoot.querySelector('[data-hero-primary-label]') : null;
    this.secondaryBtn = this.heroRoot ? this.heroRoot.querySelector('[data-hero-secondary-btn]') : null;
    this.secondaryLabelEl = this.heroRoot ? this.heroRoot.querySelector('[data-hero-secondary-label]') : null;

    this.defaults = {
      heading: this.headingEl ? this.headingEl.textContent : '',
      description: this.descriptionEl ? this.descriptionEl.innerHTML : '',
      primaryLabel: this.primaryLabelEl ? this.primaryLabelEl.textContent : '',
      primaryLink: this.primaryBtn ? this.primaryBtn.getAttribute('href') : '#',
      secondaryLabel: this.secondaryLabelEl ? this.secondaryLabelEl.textContent : '',
      secondaryLink: this.secondaryBtn ? this.secondaryBtn.getAttribute('href') : '#',
    };
  }

  applyOverrides(slide) {
    if (!slide) return;

    if (this.headingEl) {
      this.headingEl.textContent = slide.dataset.heading || this.defaults.heading;
    }

    if (this.descriptionEl) {
      const template = slide.querySelector('template[data-override-description]');
      this.descriptionEl.innerHTML = template ? template.innerHTML : this.defaults.description;
    }

    if (this.primaryBtn && this.primaryLabelEl) {
      this.primaryLabelEl.textContent = slide.dataset.primaryLabel || this.defaults.primaryLabel;
      this.primaryBtn.setAttribute('href', slide.dataset.primaryLink || this.defaults.primaryLink);
    }

    if (this.secondaryBtn && this.secondaryLabelEl) {
      this.secondaryLabelEl.textContent = slide.dataset.secondaryLabel || this.defaults.secondaryLabel;
      this.secondaryBtn.setAttribute('href', slide.dataset.secondaryLink || this.defaults.secondaryLink);
    }
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i, i > this.index ? 'next' : 'prev'));
    });

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.resumeAutoplay());

    this.handleVisibility = () => {
      if (document.hidden) this.pauseAutoplay();
      else this.resumeAutoplay();
    };
    document.addEventListener('visibilitychange', this.handleVisibility);

    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-slide]');
      if (!slide) return;
      const i = this.slides.indexOf(slide);
      if (i === -1) return;
      this.pauseAutoplay();
      this.goTo(i, i > this.index ? 'next' : 'prev');
    });

    this.addEventListener('shopify:block:deselect', () => this.resumeAutoplay());

    this.bindSwipe();
  }

  bindSwipe() {
    let startX = 0;
    let startY = 0;
    let tracking = false;

    this.track.addEventListener(
      'touchstart',
      (event) => {
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        tracking = true;
      },
      { passive: true }
    );

    this.track.addEventListener(
      'touchmove',
      (event) => {
        if (!tracking) return;
        const touch = event.touches[0];
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          event.preventDefault();
        }
      },
      { passive: false }
    );

    this.track.addEventListener('touchend', (event) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.step(deltaX < 0 ? 1 : -1);
    });
  }

  step(delta) {
    this.goTo(this.index + delta, delta > 0 ? 'next' : 'prev');
  }

  goTo(newIndex, direction) {
    const total = this.slides.length;
    const nextIndex = ((newIndex % total) + total) % total;
    if (nextIndex === this.index) return;

    this.track.dataset.direction = direction;

    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'true');
    this.slides[nextIndex].classList.add('is-active');
    this.slides[nextIndex].setAttribute('aria-hidden', 'false');

    if (this.dots[this.index]) {
      this.dots[this.index].classList.remove('is-active');
      this.dots[this.index].setAttribute('aria-current', 'false');
    }
    if (this.dots[nextIndex]) {
      this.dots[nextIndex].classList.add('is-active');
      this.dots[nextIndex].setAttribute('aria-current', 'true');
    }

    this.applyOverrides(this.slides[nextIndex]);

    if (this.captionEl) {
      this.captionEl.textContent = this.slides[nextIndex].dataset.category || '';
    }

    this.index = nextIndex;
    this.restartAutoplay();
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.paused) return;
    this.stopAutoplay();
    this.timer = window.setInterval(() => this.step(1), this.autoplayMs);
  }

  stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  pauseAutoplay() {
    this.paused = true;
    this.stopAutoplay();
  }

  resumeAutoplay() {
    this.paused = false;
    this.startAutoplay();
  }

  restartAutoplay() {
    if (!this.paused) this.startAutoplay();
  }

  setupParallax() {
    const enabled =
      this.dataset.parallax === 'true' &&
      !this.reducedMotion &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!enabled) return;

    let raf = null;
    let targetXNorm = 0;
    let targetYNorm = 0;
    const current = new Map();

    const activeLayers = () =>
      Array.from(this.querySelectorAll('.hero-editorial__slide.is-active .hero-editorial__layer'));

    const tick = () => {
      let settled = true;

      activeLayers().forEach((layer) => {
        const depth = parseFloat(layer.dataset.depth) || 0.5;
        const perLayerMax = 4 + depth * 4;
        const state = current.get(layer) || { x: 0, y: 0 };
        const tx = targetXNorm * 2 * perLayerMax;
        const ty = targetYNorm * 2 * perLayerMax * 0.6;

        state.x += (tx - state.x) * 0.08;
        state.y += (ty - state.y) * 0.08;
        current.set(layer, state);

        layer.style.setProperty('--px', `${state.x.toFixed(2)}px`);
        layer.style.setProperty('--py', `${state.y.toFixed(2)}px`);

        if (Math.abs(tx - state.x) > 0.05 || Math.abs(ty - state.y) > 0.05) settled = false;
      });

      raf = settled ? null : window.requestAnimationFrame(tick);
    };

    const ensureLoop = () => {
      if (raf === null) raf = window.requestAnimationFrame(tick);
    };

    this.handlePointerMove = (event) => {
      const rect = this.getBoundingClientRect();
      targetXNorm = (event.clientX - rect.left) / rect.width - 0.5;
      targetYNorm = (event.clientY - rect.top) / rect.height - 0.5;
      ensureLoop();
    };

    this.handlePointerLeave = () => {
      targetXNorm = 0;
      targetYNorm = 0;
      ensureLoop();
    };

    this.addEventListener('mousemove', this.handlePointerMove);
    this.addEventListener('mouseleave', this.handlePointerLeave);

    this._stopParallaxLoop = () => {
      if (raf !== null) window.cancelAnimationFrame(raf);
    };
  }

  teardownParallax() {
    if (this.handlePointerMove) this.removeEventListener('mousemove', this.handlePointerMove);
    if (this.handlePointerLeave) this.removeEventListener('mouseleave', this.handlePointerLeave);
    if (this._stopParallaxLoop) this._stopParallaxLoop();
  }
}

if (!customElements.get('hero-editorial-carousel')) {
  customElements.define('hero-editorial-carousel', HeroEditorialCarousel);
}
